import { type FC, forwardRef } from 'react'
import Image from '~/components/image/Image'
import { usePrevNextButton } from '~/hooks/usePrevNextButton'
import { useIsPC } from '~/lib/ua'
import { css } from '~/styled-system/css'
import { bleed, flex } from '~/styled-system/patterns'
import { OverlayScrollButton } from '~/ui/IconButton'
import { Section, TitleLink } from './Section'
import type { Banner } from './types'

type Props = {
  specialFeaturesAndCampaigns: Banner[]
}

export const SpecialFeaturesAndCampaigns: FC<Props> =
  import.meta.env.VITE_MODE === 'ppg'
    ? EmptySpecialFeaturesAndCampaigns
    : IkyuSpecialFeaturesAndCampaigns

function EmptySpecialFeaturesAndCampaigns(_: Props) {
  return null
}

function IkyuSpecialFeaturesAndCampaigns({ specialFeaturesAndCampaigns }: Props) {
  const isPc = useIsPC()
  const SpecialFeaturesAndCampaigns = isPc
    ? PcSpecialFeaturesAndCampaigns
    : SdSpecialFeaturesAndCampaigns
  return <SpecialFeaturesAndCampaigns specialFeaturesAndCampaigns={specialFeaturesAndCampaigns} />
}

function PcSpecialFeaturesAndCampaigns({ specialFeaturesAndCampaigns }: Props) {
  const { ref, next, prev, isNextDisabled, isPrevDisabled } = usePrevNextButton()
  return (
    <Section title={<TitleLink title="キャンペーン・特集" href="/rsSpcl" />}>
      <div className={css({ position: 'relative' })} role="group">
        {!isPrevDisabled && <OverlayScrollButton direction="left" onClick={prev} />}
        <Body specialFeaturesAndCampaigns={specialFeaturesAndCampaigns} ref={ref} />
        {!isNextDisabled && <OverlayScrollButton direction="right" onClick={next} />}
      </div>
    </Section>
  )
}

function SdSpecialFeaturesAndCampaigns({ specialFeaturesAndCampaigns }: Props) {
  return (
    <Section
      title={<TitleLink title="キャンペーン・特集" href="/rsSpcl" withPadding={false} />}
      withPadding={false}
    >
      <Body specialFeaturesAndCampaigns={specialFeaturesAndCampaigns} />
    </Section>
  )
}

const Body = forwardRef<HTMLDivElement, Props>((props, ref) => (
  <div
    className={css(
      flex.raw({
        columnGap: '3',
        overflow: { base: 'auto', _pc: 'hidden' },
        pb: '3',
        _sd: { px: '3' },
        _pc: { px: '[2px]' },
      }),
      { _sd: bleed.raw({ inline: '3' }) }
    )}
    ref={ref}
  >
    {props.specialFeaturesAndCampaigns.map((data) => {
      return (
        <div
          key={data.object_id}
          data-object-id={data.object_id}
          className={css({
            rounded: 'md',
            shadow: 'outline.sm',
            flexShrink: 0,
            w: '[240px]',
          })}
        >
          <a href={data.link} className={css({ display: 'block' })}>
            <Image
              className={css({ roundedTop: 'md' })}
              alt={data.image.alt}
              src={data.image.url}
              width={240}
              height={120}
            />
            <div className={css({ p: '3', spaceY: '1', minHeight: '[90px]' })}>
              <h3
                className={css({
                  fontSize: 'lg',
                  color: 'gray.800',
                  fontWeight: 'bold',
                })}
              >
                {data.title}
              </h3>
              <p
                className={css({
                  fontSize: 'sm',
                  color: 'gray.700',
                  whiteSpace: 'normal',
                  lineHeight: 'relaxed',
                })}
              >
                {data.description}
              </p>
            </div>
          </a>
        </div>
      )
    })}
  </div>
))
Body.displayName = 'SpecialFeaturesAndCampaignsBody'
